/**
 * Vue 3 Composable for Profile and Password Management
 *
 * Provides API methods for:
 * - Fetching user profile
 * - Updating profile (avatar)
 * - Changing password
 * - Setting password (OAuth users)
 * - Password reset flow
 */

import { ref } from 'vue'

// API URL configuration
const isDevPort =
  window.location.port && !['80', '443', ''].includes(window.location.port)
const API_URL =
  window.location.hostname === 'localhost' && isDevPort
    ? `${window.location.protocol}//localhost:8000/api`
    : `${window.location.origin}/api`

/**
 * Profile response from the API
 */
export interface ProfileResponse {
  id: string
  username: string
  email: string
  profile_picture: string
  display_name: string | null
  auth_provider: string
  created_at: string
  games_played: number
  games_won: number
  total_score: number
  password_changed_at: string | null
  has_password: boolean
}

/**
 * Error response structure
 */
interface ApiError {
  detail: string | { msg: string } | Array<{ msg: string }>
  message?: string
}

/**
 * Parse error response from API
 */
function parseApiError(data: ApiError): string {
  if (typeof data.detail === 'string') {
    return data.detail
  }
  if (Array.isArray(data.detail)) {
    return data.detail.map((e) => e.msg || JSON.stringify(e)).join(', ')
  }
  if (data.detail && typeof data.detail === 'object') {
    return data.detail.msg || JSON.stringify(data.detail)
  }
  if (data.message) {
    return data.message
  }
  return 'An error occurred'
}

/**
 * Get authorization header with access token
 */
function getAuthHeader(): Record<string, string> {
  const token = localStorage.getItem('access_token')
  return token ? { Authorization: `Bearer ${token}` } : {}
}

/**
 * Profile and password management composable
 */
export function useProfile() {
  const loading = ref(false)
  const error = ref<string | null>(null)

  /**
   * Fetch current user's profile
   */
  async function getProfile(): Promise<ProfileResponse> {
    loading.value = true
    error.value = null

    try {
      const response = await fetch(`${API_URL}/profile/me`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader(),
        },
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(parseApiError(data))
      }

      return data as ProfileResponse
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load profile'
      error.value = message
      throw new Error(message)
    } finally {
      loading.value = false
    }
  }

  /**
   * Update profile (currently just avatar)
   */
  async function updateProfile(profilePicture: string): Promise<ProfileResponse> {
    loading.value = true
    error.value = null

    try {
      const response = await fetch(`${API_URL}/profile/me`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader(),
        },
        body: JSON.stringify({ profile_picture: profilePicture }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(parseApiError(data))
      }

      return data as ProfileResponse
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to update profile'
      error.value = message
      throw new Error(message)
    } finally {
      loading.value = false
    }
  }

  /**
   * Change password (for users with existing password)
   */
  async function changePassword(
    currentPassword: string,
    newPassword: string,
    confirmPassword: string
  ): Promise<void> {
    loading.value = true
    error.value = null

    try {
      const response = await fetch(`${API_URL}/profile/me/password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader(),
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword,
          confirm_password: confirmPassword,
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(parseApiError(data))
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to change password'
      error.value = message
      throw new Error(message)
    } finally {
      loading.value = false
    }
  }

  /**
   * Set password (for OAuth users without password)
   */
  async function setPassword(
    newPassword: string,
    confirmPassword: string
  ): Promise<void> {
    loading.value = true
    error.value = null

    try {
      const response = await fetch(`${API_URL}/profile/me/set-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader(),
        },
        body: JSON.stringify({
          new_password: newPassword,
          confirm_password: confirmPassword,
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(parseApiError(data))
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to set password'
      error.value = message
      throw new Error(message)
    } finally {
      loading.value = false
    }
  }

  /**
   * Request password reset email
   */
  async function requestPasswordReset(email: string): Promise<string> {
    loading.value = true
    error.value = null

    try {
      const response = await fetch(`${API_URL}/auth/password-reset/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(parseApiError(data))
      }

      return data.message
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to request password reset'
      error.value = message
      throw new Error(message)
    } finally {
      loading.value = false
    }
  }

  /**
   * Validate password reset token
   */
  async function validateResetToken(
    token: string
  ): Promise<{ valid: boolean; message: string }> {
    loading.value = true
    error.value = null

    try {
      const response = await fetch(
        `${API_URL}/auth/password-reset/validate/${token}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      )

      const data = await response.json()

      if (!response.ok) {
        throw new Error(parseApiError(data))
      }

      return data
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to validate token'
      error.value = message
      return { valid: false, message }
    } finally {
      loading.value = false
    }
  }

  /**
   * Confirm password reset with new password
   */
  async function confirmPasswordReset(
    token: string,
    newPassword: string,
    confirmPassword: string
  ): Promise<string> {
    loading.value = true
    error.value = null

    try {
      const response = await fetch(`${API_URL}/auth/password-reset/confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          new_password: newPassword,
          confirm_password: confirmPassword,
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(parseApiError(data))
      }

      return data.message
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to reset password'
      error.value = message
      throw new Error(message)
    } finally {
      loading.value = false
    }
  }

  return {
    // State
    loading,
    error,

    // Profile methods
    getProfile,
    updateProfile,

    // Password methods
    changePassword,
    setPassword,

    // Password reset methods
    requestPasswordReset,
    validateResetToken,
    confirmPasswordReset,
  }
}
